import React, { useEffect, useState } from "react";
import emily from "./assets/emily.png";
import preloadassets from "./assets/preloadassets";
import MobileGate from "./components/mobilegate";
import Preloader from "./components/preloader";
import Routing from "./components/routing";

const MOBILE_BREAKPOINT = 900;
const PRELOADER_MIN_DURATION = 2200;
const FONT_TIMEOUT = 1500;

const getIsMobileViewport = () => {
  if (typeof window === "undefined") {
    return false;
  }

  return window.innerWidth <= MOBILE_BREAKPOINT;
};

const loadimageasset = (src, onfinish) =>
  new Promise((resolve) => {
    const image = new Image();
    let settled = false;

    const complete = () => {
      if (settled) {
        return;
      }

      settled = true;
      onfinish();
      resolve();
    };

    image.onload = complete;
    image.onerror = complete;
    image.src = src;

    if (image.complete) {
      window.setTimeout(complete, 0);
    }
  });

const App = () => {
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(getIsMobileViewport);
  const [hasPreloaded, setHasPreloaded] = useState(false);
  const [isPreloading, setIsPreloading] = useState(!getIsMobileViewport());
  const [preloadProgress, setPreloadProgress] = useState(0);

  useEffect(() => {
    const mediaQuery = window.matchMedia(
      `(max-width: ${MOBILE_BREAKPOINT}px)`
    );

    const handleChange = (event) => {
      setIsMobileViewport(event.matches);
    };

    handleChange(mediaQuery);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);

      return () => {
        mediaQuery.removeEventListener("change", handleChange);
      };
    }

    mediaQuery.addListener(handleChange);

    return () => {
      mediaQuery.removeListener(handleChange);
    };
  }, []);

  useEffect(() => {
    document.title = "Emily Dickinson";

    const iconLink =
      document.querySelector("link[rel='icon']") ||
      document.createElement("link");

    iconLink.setAttribute("rel", "icon");
    iconLink.setAttribute("type", "image/png");
    iconLink.setAttribute("href", emily);

    if (!iconLink.parentNode) {
      document.head.appendChild(iconLink);
    }

    const appleTouchIcon =
      document.querySelector("link[rel='apple-touch-icon']") ||
      document.createElement("link");

    appleTouchIcon.setAttribute("rel", "apple-touch-icon");
    appleTouchIcon.setAttribute("href", emily);

    if (!appleTouchIcon.parentNode) {
      document.head.appendChild(appleTouchIcon);
    }
  }, []);

  useEffect(() => {
    if (isMobileViewport || hasPreloaded) {
      return;
    }

    let active = true;
    let loadedCount = 0;
    const timeoutIds = [];
    const totalItems = preloadassets.length + 1;
    const startTime = Date.now();

    setIsPreloading(true);
    setPreloadProgress(0);

    const markLoaded = () => {
      loadedCount += 1;

      if (!active) {
        return;
      }

      setPreloadProgress(
        Math.min(100, Math.round((loadedCount / totalItems) * 100))
      );
    };

    const loadFonts = () => {
      if (typeof document === "undefined" || !document.fonts?.ready) {
        markLoaded();
        return Promise.resolve();
      }

      return Promise.race([
        document.fonts.ready,
        new Promise((resolve) => {
          const timeoutId = window.setTimeout(resolve, FONT_TIMEOUT);
          timeoutIds.push(timeoutId);
        })
      ]).then(() => {
        markLoaded();
      });
    };

    Promise.allSettled([
      loadFonts(),
      ...preloadassets.map((src) => loadimageasset(src, markLoaded))
    ]).then(() => {
      const remainingTime = Math.max(
        0,
        PRELOADER_MIN_DURATION - (Date.now() - startTime)
      );

      const timeoutId = window.setTimeout(() => {
        if (!active) {
          return;
        }

        setPreloadProgress(100);
        setHasPreloaded(true);
        setIsPreloading(false);
      }, remainingTime);

      timeoutIds.push(timeoutId);
    });

    return () => {
      active = false;
      timeoutIds.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
    };
  }, [hasPreloaded, isMobileViewport]);

  if (isMobileViewport) {
    return <MobileGate />;
  }

  if (isPreloading) {
    return <Preloader progress={preloadProgress} />;
  }

  return (
    <Routing
      setSoundEnabled={setSoundEnabled}
      soundEnabled={soundEnabled}
    />
  );
};

export default App;
